/**
 * Display-formatting helpers shared across portal pages (RLY-024).
 *
 * Pure functions — no React, no DOM, no time.
 */
import type { RunResponse } from "@simpliwork/api-schema";

/** Convert micro-USD (1e-6 USD) to a human "$0.0042" string. */
export function formatCostMicros(micros: number): string {
  if (!Number.isFinite(micros) || micros < 0) return "—";
  const dollars = micros / 1_000_000;
  if (dollars === 0) return "$0.00";
  if (dollars < 0.01) return `$${dollars.toFixed(4)}`;
  return `$${dollars.toFixed(2)}`;
}

/** Compute "duration in milliseconds → human" with millisecond precision. */
export function formatDurationMs(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const restSeconds = Math.round(seconds - minutes * 60);
  return `${minutes}m ${restSeconds}s`;
}

/** Compute the duration of a run in ms; null if not yet started/completed. */
export function runDurationMs(
  run: Pick<RunResponse, "started_at" | "completed_at">,
): number | null {
  if (!run.started_at || !run.completed_at) return null;
  const start = Date.parse(run.started_at);
  const end = Date.parse(run.completed_at);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return end - start;
}

/** Map a `RunStatus` to a Badge variant (so display stays consistent). */
export function runStatusToBadgeVariant(
  status: RunResponse["status"],
): "default" | "success" | "warning" | "destructive" | "secondary" {
  switch (status) {
    case "completed":
      return "success";
    case "running":
      return "default";
    case "pending":
      return "secondary";
    case "failed":
    case "timeout":
      return "destructive";
    case "cancelled":
      return "warning";
    default:
      return "secondary";
  }
}

/** Format an ISO timestamp as a friendly local string, e.g. "Apr 26, 14:32". */
export function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
